import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = { url: String }
  static targets = ['input', 'results', 'hidden']

  connect() {
    this.onInput = this.onInput.bind(this)
    this.onClickOutside = this.onClickOutside.bind(this)
    this.onScrollOrResize = this.onScrollOrResize.bind(this)

    this.inputTarget.addEventListener('input', this.onInput)
    this.inputTarget.addEventListener('focus', () => this.positionPortal())
    document.addEventListener('click', this.onClickOutside)
    window.addEventListener('resize', this.onScrollOrResize)
    window.addEventListener('scroll', this.onScrollOrResize, true)

    this.debounceTimer = null
    this.createPortal()
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', this.onInput)
    document.removeEventListener('click', this.onClickOutside)
    window.removeEventListener('resize', this.onScrollOrResize)
    window.removeEventListener('scroll', this.onScrollOrResize, true)
    this.destroyPortal()
  }

  onInput(event) {
    const q = event.target.value.trim()
    clearTimeout(this.debounceTimer)
    if (!q) {
      this.hideResults()
      this.hiddenTarget.value = ''
      return
    }
    this.debounceTimer = setTimeout(() => this.search(q), 200)
  }

  async search(q) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', q)
      const res = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!res.ok) throw new Error('Search failed')
      const data = await res.json()
      this.renderResults(data)
    } catch {
      this.renderResults([])
    }
  }

  createPortal() {
    this.portalEl = document.createElement('ul')
    this.portalEl.className =
      'fixed z-[1000] mt-1 rounded-md bg-white dark:bg-gray-800 shadow ring-1 ring-black/5 max-h-60 overflow-auto hidden'
    this.portalEl.style.minWidth = '200px'
    document.body.appendChild(this.portalEl)
  }

  destroyPortal() {
    if (this.portalEl) {
      this.portalEl.remove()
      this.portalEl = null
    }
  }

  positionPortal() {
    if (!this.portalEl || this.portalEl.classList.contains('hidden')) return
    const rect = this.inputTarget.getBoundingClientRect()
    this.portalEl.style.top = `${rect.bottom + window.scrollY}px`
    this.portalEl.style.left = `${rect.left + window.scrollX}px`
    this.portalEl.style.width = `${rect.width}px`
  }

  onScrollOrResize() {
    this.positionPortal()
  }

  renderResults(items) {
    const ul = this.portalEl
    ul.innerHTML = ''
    if (!items.length) {
      this.hideResults()
      return
    }
    items.forEach((item) => {
      const li = document.createElement('li')
      li.textContent = item.label
      li.dataset.id = item.id
      li.className = 'cursor-pointer px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm'
      // Use pointerdown to work better on mobile (fires before blur)
      li.addEventListener('pointerdown', (e) => {
        e.preventDefault()
        this.select(item)
      })
      // Fallback for click
      li.addEventListener('click', (e) => {
        e.preventDefault()
        this.select(item)
      })
      ul.appendChild(li)
    })
    ul.classList.remove('hidden')
    this.positionPortal()
  }

  select(item) {
    this.inputTarget.value = item.label
    this.hiddenTarget.value = item.id
    this.hideResults()
    // Optionally blur to dismiss mobile keyboard
    this.inputTarget.blur()
    // Dispatch an event so validators can re-check
    this.element.dispatchEvent(new CustomEvent('lodge:selected', { bubbles: true }))
  }

  hideResults() {
    if (this.portalEl) this.portalEl.classList.add('hidden')
  }

  onClickOutside(event) {
    const clickedInsideController = this.element.contains(event.target)
    const clickedInsidePortal = this.portalEl?.contains(event.target)
    if (!clickedInsideController && !clickedInsidePortal) this.hideResults()
  }
}
